/**
 * UI catalog for AI conversation signals (single frontend source).
 * Keep aligned with: app/modules/openAi/conversation_signal_catalog.py CONVERSATION_SIGNAL_UI_CATALOG
 * API mirror: GET /chats/meta/conversation-signals/
 */

export type ConversationSignal =
  | 'RFI'
  | 'RFI + USE_CASE_GAP'
  | 'FAST_CONVERTER'
  | 'VAGUE_POSITIVE'
  | 'SPECIFIC_OBJECTION'
  | 'WRONG_PERSON'
  | 'USE_CASE_GAP'
  | 'REFERRAL_OPPORTUNITY'
  | 'NOT_A_MATCH'
  | 'PRODUCT_MISUNDERSTOOD'
  | 'TOO_EARLY'
  | 'UNANSWERED_QUESTION';

export type SignalUiCategory = 'buying' | 'qualifying' | 'objection';

export interface ConversationSignalFilterColors {
  dot: string;
  bg: string;
  text: string;
  border: string;
}

export interface ConversationSignalUiEntry {
  key: ConversationSignal;
  label: string;
  description: string;
  category: SignalUiCategory;
  filter: ConversationSignalFilterColors;
}

/** Stable order for filters and display */
export const CONVERSATION_SIGNAL_UI_CATALOG: ConversationSignalUiEntry[] = [
  {
    key: 'RFI',
    label: 'RFI',
    description: 'Asked about product, technology, application, or use case',
    category: 'buying',
    filter: { dot: '#4caf50', bg: '#e8f5e9', text: '#2e7d32', border: '#66bb6a' },
  },
  {
    key: 'RFI + USE_CASE_GAP',
    label: 'RFI + use case gap',
    description: 'Technical or fit question plus an application outside campaign coverage',
    category: 'buying',
    filter: { dot: '#8bc34a', bg: '#f1f8e9', text: '#33691e', border: '#8bc34a' },
  },
  {
    key: 'FAST_CONVERTER',
    label: 'Fast Converter',
    description: 'Strong intent to move forward (demo, pricing, next steps)',
    category: 'buying',
    filter: { dot: '#66bb6a', bg: '#e8f5e9', text: '#2e7d32', border: '#66bb6a' },
  },
  {
    key: 'VAGUE_POSITIVE',
    label: 'Vague Positive',
    description: 'Positive but non-specific, needs qualification',
    category: 'qualifying',
    filter: { dot: '#ffa726', bg: '#fff8e1', text: '#f57f17', border: '#ffa726' },
  },
  {
    key: 'SPECIFIC_OBJECTION',
    label: 'Specific Objection',
    description: 'Concrete pushback (competitor, budget, timing, fit)',
    category: 'objection',
    filter: { dot: '#ef5350', bg: '#ffebee', text: '#c62828', border: '#ef5350' },
  },
  {
    key: 'WRONG_PERSON',
    label: 'Wrong Person',
    description: 'Not the right contact or topic outside their role',
    category: 'objection',
    filter: { dot: '#e53935', bg: '#ffebee', text: '#c62828', border: '#ef5350' },
  },
  {
    key: 'USE_CASE_GAP',
    label: 'Use case gap',
    description: 'Application or workflow not clearly covered in campaign messaging',
    category: 'qualifying',
    filter: { dot: '#ffb300', bg: '#fff8e1', text: '#f57f17', border: '#ffb300' },
  },
  {
    key: 'REFERRAL_OPPORTUNITY',
    label: 'Referral opportunity',
    description: 'Pointed to another person, team, or better path',
    category: 'buying',
    filter: { dot: '#42a5f5', bg: '#e3f2fd', text: '#1565c0', border: '#42a5f5' },
  },
  {
    key: 'NOT_A_MATCH',
    label: 'Not a match',
    description: 'Clear mismatch with campaign ICP or offer',
    category: 'objection',
    filter: { dot: '#78909c', bg: '#eceff1', text: '#455a64', border: '#78909c' },
  },
  {
    key: 'PRODUCT_MISUNDERSTOOD',
    label: 'Product misunderstood',
    description: 'Misread what the product or service actually is',
    category: 'qualifying',
    filter: { dot: '#7e57c2', bg: '#ede7f6', text: '#4527a0', border: '#7e57c2' },
  },
  {
    key: 'TOO_EARLY',
    label: 'Too early',
    description: 'Timing not right yet; early stage or defer decision',
    category: 'qualifying',
    filter: { dot: '#26a69a', bg: '#e0f2f1', text: '#00695c', border: '#26a69a' },
  },
  {
    key: 'UNANSWERED_QUESTION',
    label: 'Unanswered question',
    description: 'They asked something we have not addressed in-thread',
    category: 'buying',
    filter: { dot: '#29b6f6', bg: '#e1f5fe', text: '#0277bd', border: '#29b6f6' },
  },
];

const byKey: Record<ConversationSignal, ConversationSignalUiEntry> = {} as Record<
  ConversationSignal,
  ConversationSignalUiEntry
>;
for (const e of CONVERSATION_SIGNAL_UI_CATALOG) {
  byKey[e.key] = e;
}

export function getConversationSignalUi(key: string): ConversationSignalUiEntry | undefined {
  return byKey[key as ConversationSignal];
}

/** Inbox filter chips / EntityMultiSelect options */
export const SIGNAL_FILTER_OPTIONS = CONVERSATION_SIGNAL_UI_CATALOG.map((e) => ({
  value: e.key,
  label: e.label,
  description: e.description,
  dot: e.filter.dot,
  bg: e.filter.bg,
  text: e.filter.text,
  border: e.filter.border,
}));
